import React from 'react';
import { motion } from 'framer-motion';
import { Users, User, Building2, Calendar } from 'lucide-react';

export const ContentSkeleton = ({ lines = 3, className = '' }) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {[...Array(lines)].map((_, index) => (
        <div
          key={index}
          className={`h-4 animate-pulse rounded bg-slate-200 ${
            index === lines - 1 ? 'w-3/4' : 'w-full'
          }`}
        />
      ))}
    </div>
  );
};

export const CardSkeleton = ({ className = '' }) => {
  return (
    <div className={`rounded-xl border bg-white p-6 ${className}`}>
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 animate-pulse rounded-full bg-slate-200 p-2"></div>
          <div className="space-y-2">
            <div className="h-4 w-24 animate-pulse rounded bg-slate-200"></div>
            <div className="h-3 w-20 animate-pulse rounded bg-slate-200"></div>
          </div>
        </div>
        <div className="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
      </div>
      <ContentSkeleton lines={3} />
    </div>
  );
};

export const StatsCardSkeleton = ({ icon: Icon = Users, className = '' }) => {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      className={`rounded-xl border bg-white p-4 ${className}`}
    >
      <div className="flex items-center space-x-3">
        <div className="h-10 w-10 animate-pulse rounded-lg bg-slate-200 p-2"></div>
        <div className="space-y-2">
          <div className="h-3 w-16 animate-pulse rounded bg-slate-200"></div>
          <div className="h-6 w-8 animate-pulse rounded bg-slate-200"></div>
        </div>
      </div>
    </motion.div>
  );
};

export const TableSkeleton = ({ rows = 5, columns = 4, className = '' }) => {
  return (
    <div className={`overflow-hidden rounded-xl border bg-white ${className}`}>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="border-b bg-slate-50">
            <tr>
              {[...Array(columns)].map((_, index) => (
                <th key={index} className="px-6 py-3">
                  <div className="h-3 w-16 animate-pulse rounded bg-slate-200"></div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {[...Array(rows)].map((_, rowIndex) => (
              <tr key={rowIndex}>
                {[...Array(columns)].map((_, cellIndex) => (
                  <td key={cellIndex} className="px-6 py-4">
                    <div className="space-y-2">
                      <div className="h-4 w-20 animate-pulse rounded bg-slate-200"></div>
                      {cellIndex === 0 && (
                        <div className="h-3 w-16 animate-pulse rounded bg-slate-200"></div>
                      )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export const FormSkeleton = ({ fields = 4, className = '' }) => {
  return (
    <div className={`space-y-4 ${className}`}>
      {[...Array(fields)].map((_, index) => (
        <div key={index} className="space-y-2">
          <div className="h-4 w-20 animate-pulse rounded bg-slate-200"></div>
          <div className="h-10 w-full animate-pulse rounded-lg bg-slate-200"></div>
        </div>
      ))}
      <div className="flex justify-end space-x-3 pt-4">
        <div className="h-10 w-20 animate-pulse rounded-lg bg-slate-200"></div>
        <div className="h-10 w-24 animate-pulse rounded-lg bg-slate-200"></div>
      </div>
    </div>
  );
};

export const DashboardSkeleton = () => {
  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <div className="h-8 w-48 animate-pulse rounded-lg bg-slate-200"></div>
          <div className="h-4 w-64 animate-pulse rounded-lg bg-slate-200"></div>
        </div>
        <div className="h-10 w-32 animate-pulse rounded-xl bg-slate-200"></div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatsCardSkeleton icon={Users} />
        <StatsCardSkeleton icon={User} />
        <StatsCardSkeleton icon={Building2} />
        <StatsCardSkeleton icon={Calendar} />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <CardSkeleton />
        <CardSkeleton />
      </div>

      <TableSkeleton />
    </div>
  );
};

export const TeamMemberSkeleton = ({ variant = 'card' }) => {
  if (variant === 'list') {
    return (
      <tr>
        <td className="whitespace-nowrap px-6 py-4">
          <div className="flex items-center">
            <div className="mr-3 h-10 w-10 animate-pulse rounded-full bg-slate-200 p-2"></div>
            <div>
              <div className="h-4 w-24 animate-pulse rounded bg-slate-200"></div>
              <div className="h-3 w-20 animate-pulse rounded bg-slate-200"></div>
            </div>
          </div>
        </td>
        <td className="whitespace-nowrap px-6 py-4">
          <div className="h-6 w-16 animate-pulse rounded-full bg-slate-200"></div>
        </td>
        <td className="whitespace-nowrap px-6 py-4">
          <div className="h-4 w-32 animate-pulse rounded bg-slate-200"></div>
        </td>
        <td className="whitespace-nowrap px-6 py-4">
          <div className="h-5 w-16 animate-pulse rounded-full bg-slate-200"></div>
        </td>
        <td className="whitespace-nowrap px-6 py-4">
          <div className="h-4 w-20 animate-pulse rounded bg-slate-200"></div>
        </td>
        <td className="whitespace-nowrap px-6 py-4">
          <div className="flex space-x-2">
            <div className="h-6 w-8 animate-pulse rounded bg-slate-200"></div>
            <div className="h-6 w-16 animate-pulse rounded bg-slate-200"></div>
            <div className="h-6 w-12 animate-pulse rounded bg-slate-200"></div>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-xl border bg-white p-6"
    >
      <div className="mb-4 flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 animate-pulse rounded-full bg-slate-200 p-2"></div>
          <div className="space-y-2">
            <div className="h-4 w-24 animate-pulse rounded bg-slate-200"></div>
            <div className="h-3 w-20 animate-pulse rounded bg-slate-200"></div>
          </div>
        </div>
        <div className="flex space-x-1">
          <div className="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
          <div className="h-6 w-6 animate-pulse rounded bg-slate-200"></div>
        </div>
      </div>

      <div className="mb-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="h-3 w-12 animate-pulse rounded bg-slate-200"></div>
          <div className="h-6 w-16 animate-pulse rounded-full bg-slate-200"></div>
        </div>
        <div className="flex items-center justify-between">
          <div className="h-3 w-10 animate-pulse rounded bg-slate-200"></div>
          <div className="flex items-center space-x-1">
            <div className="h-3 w-3 animate-pulse rounded bg-slate-200"></div>
            <div className="h-3 w-24 animate-pulse rounded bg-slate-200"></div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="h-3 w-12 animate-pulse rounded bg-slate-200"></div>
          <div className="flex items-center space-x-1">
            <div className="h-3 w-3 animate-pulse rounded bg-slate-200"></div>
            <div className="h-3 w-20 animate-pulse rounded bg-slate-200"></div>
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="h-3 w-8 animate-pulse rounded bg-slate-200"></div>
          <div className="h-5 w-16 animate-pulse rounded-full bg-slate-200"></div>
        </div>
      </div>

      <div className="flex space-x-2">
        <div className="h-8 flex-1 animate-pulse rounded-lg bg-slate-200"></div>
        <div className="h-8 flex-1 animate-pulse rounded-lg bg-slate-200"></div>
        <div className="h-8 w-8 animate-pulse rounded-lg bg-slate-200"></div>
      </div>
    </motion.div>
  );
};

export const PageSkeleton = ({ title = 'Loading...' }) => {
  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <DashboardSkeleton />
      </div>
    </div>
  );
};

export const MarbleLoader = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'h-12 w-12',
    md: 'h-16 w-16',
    lg: 'h-20 w-20',
  };

  const marbleSize = {
    sm: 'h-3 w-3',
    md: 'h-4 w-4',
    lg: 'h-5 w-5',
  };

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div className={`relative ${sizeClasses[size]}`}>
        {/* Marble 1 - Top */}
        <motion.div
          className={`absolute left-1/2 top-0 -translate-x-1/2 rounded-full bg-blue-600 ${marbleSize[size]}`}
          animate={{
            y: [0, size === 'sm' ? 36 : size === 'md' ? 48 : 60, 0],
            x: [0, size === 'sm' ? 18 : size === 'md' ? 24 : 30, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        {/* Marble 2 - Right */}
        <motion.div
          className={`absolute right-0 top-1/2 -translate-y-1/2 rounded-full bg-gray-400 ${marbleSize[size]}`}
          animate={{
            x: [0, size === 'sm' ? -36 : size === 'md' ? -48 : -60, 0],
            y: [0, size === 'sm' ? -18 : size === 'md' ? -24 : -30, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.2,
          }}
        />
        {/* Marble 3 - Bottom */}
        <motion.div
          className={`absolute bottom-0 left-1/2 -translate-x-1/2 rounded-full bg-blue-500 ${marbleSize[size]}`}
          animate={{
            y: [0, size === 'sm' ? -36 : size === 'md' ? -48 : -60, 0],
            x: [0, size === 'sm' ? -18 : size === 'md' ? -24 : -30, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.4,
          }}
        />
        {/* Marble 4 - Left */}
        <motion.div
          className={`absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-gray-500 ${marbleSize[size]}`}
          animate={{
            x: [0, size === 'sm' ? 36 : size === 'md' ? 48 : 60, 0],
            y: [0, size === 'sm' ? 18 : size === 'md' ? 24 : 30, 0],
            scale: [1, 0.8, 1],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 0.6,
          }}
        />
        {/* Center marble */}
        <motion.div
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-blue-600 ${marbleSize[size]}`}
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
      </div>
    </div>
  );
};

export default {
  ContentSkeleton,
  CardSkeleton,
  StatsCardSkeleton,
  TableSkeleton,
  FormSkeleton,
  DashboardSkeleton,
  TeamMemberSkeleton,
  PageSkeleton,
  MarbleLoader,
};
